import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Save, ExternalLink, FileText } from "lucide-react";
import { API_URL } from "../utils/api";

/**
 * BlogManager — Embeddable admin panel for the TaxSetu blog / Regulatory Pulse
 * feed. Takes an admin `password` prop (already collected by the parent page)
 * and provides full CRUD over `regulatory_pulse_posts`.
 *
 * Same collection powers the top ticker + the "TaxSetu AI Blog" grid on the
 * landing page, so deleting here removes the post from BOTH surfaces.
 */

const CATEGORIES = [
  "Income Tax", "GST", "Labour Law", "Companies Act",
  "FEMA/RBI", "Capital Markets", "Case Laws", "Stock Markets", "Mixed",
];

const STATUSES = [
  "Reported", "Proposed", "Recommended",
  "Cabinet-Approved", "Notified", "Effective",
];

const empty = {
  title: "",
  category: "Income Tax",
  status: "Reported",
  notification_ref: "",
  what_changed: "",
  impact: "",
  recommendation: "",
  teaser: "",
};

export default function BlogManager({ password }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSlug, setEditingSlug] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const headers = { "X-Admin-Password": password };

  const load = async () => {
    if (!password) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/pulse/admin/list`, { headers });
      if (!res.ok) throw new Error("Failed to load");
      const data = await res.json();
      setPosts(data.posts || []);
    } catch (e) {
      toast.error("Could not fetch posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const openNew = () => {
    setForm(empty);
    setEditingSlug(null);
    setShowForm(true);
  };

  const openEdit = (post) => {
    setForm({
      title: post.title || "",
      category: post.category || "Mixed",
      status: post.status || "Reported",
      notification_ref: post.notification_ref || "",
      what_changed: post.what_changed || "",
      impact: post.impact || "",
      recommendation: post.recommendation || "",
      teaser: post.teaser || "",
    });
    setEditingSlug(post.slug);
    setShowForm(true);
  };

  const submit = async () => {
    for (const k of ["title", "what_changed", "impact", "recommendation"]) {
      if (!form[k] || form[k].trim().length < 10) {
        toast.error(`Please fill "${k.replace("_", " ")}" (min 10 chars)`);
        return;
      }
    }
    setSaving(true);
    try {
      const url = editingSlug
        ? `${API_URL}/pulse/admin/${editingSlug}`
        : `${API_URL}/pulse/admin/create`;
      const method = editingSlug ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Save failed");
      }
      toast.success(editingSlug ? "Post updated" : "Post published");
      setShowForm(false);
      setForm(empty);
      setEditingSlug(null);
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this post permanently? It will disappear from the ticker AND the blog grid.")) return;
    try {
      const res = await fetch(`${API_URL}/pulse/admin/${slug}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Delete failed");
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-cyan-300" />
            Blog & Ticker Manager
          </h3>
          <p className="text-[12.5px] text-slate-400 mt-0.5">
            Every post here appears on the top live ticker AND in the landing-page blog grid. Delete from here to remove it from both surfaces instantly.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-cyan-500 hover:bg-cyan-400 text-slate-950"
          data-testid="blog-manager-new-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New blog post
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-800/40 animate-pulse" />
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-xl border border-slate-700 bg-slate-800/40 p-8 text-center text-slate-500">
          No posts yet. Tap “New blog post” to publish the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {posts.map((p) => (
            <div
              key={p.slug}
              className="group rounded-xl border border-slate-700 bg-slate-800/40 p-4 hover:border-slate-600 transition-colors"
              data-testid={`blog-manager-item-${p.slug}`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[9.5px] font-bold uppercase tracking-widest text-cyan-300 border border-cyan-500/30 rounded-full px-2 py-0.5">
                  {p.category}
                </span>
                {p.status && (
                  <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md border border-white/15 text-slate-300 bg-white/[0.04]">
                    {p.status}
                  </span>
                )}
                {p.author && (
                  <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300">
                    · {p.author}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-500 font-mono uppercase">
                  {p.published_at
                    ? new Date(p.published_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      })
                    : ""}
                </span>
              </div>
              <p className="text-[13.5px] font-semibold text-white leading-snug mb-3 line-clamp-2">
                {p.title}
              </p>
              <div className="flex items-center gap-3 text-[12px]">
                <button
                  onClick={() => openEdit(p)}
                  className="text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
                  data-testid={`blog-manager-edit-${p.slug}`}
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
                <button
                  onClick={() => remove(p.slug)}
                  className="text-rose-300 hover:text-rose-200 flex items-center gap-1"
                  data-testid={`blog-manager-delete-${p.slug}`}
                  title="Remove from ticker and blog grid"
                >
                  <Trash2 className="w-3 h-3" />
                  Delete
                </button>
                <a
                  href={`/pulse/${p.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-slate-400 hover:text-slate-200 flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  View
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Slide-over form */}
      {showForm && (
        <div
          className="fixed inset-0 z-[120] bg-slate-950/85 backdrop-blur-md flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h2 className="text-lg font-bold text-white">
                {editingSlug ? "Edit blog post" : "New blog post"}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white p-1 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Title (auto-prefixed with "TaxSetu Insight:" if you skip it)
                </label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Cabinet approves EPFO wage ceiling hike…"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="blog-manager-form-title"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                    Category
                  </label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-10 rounded-md bg-slate-900 border border-slate-700 text-white px-3 text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                    Status
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full h-10 rounded-md bg-slate-900 border border-slate-700 text-white px-3 text-sm"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">
                  Official reference (optional)
                </label>
                <Input
                  value={form.notification_ref}
                  onChange={(e) => setForm({ ...form, notification_ref: e.target.value })}
                  placeholder="CBDT Notification 45/2026 or PIB press release dt. 15 Sep 2026"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">What changed *</label>
                <textarea
                  value={form.what_changed}
                  onChange={(e) => setForm({ ...form, what_changed: e.target.value })}
                  rows={4}
                  placeholder="Explain the notification / law / limit change…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Impact on taxpayers & practitioners *</label>
                <textarea
                  value={form.impact}
                  onChange={(e) => setForm({ ...form, impact: e.target.value })}
                  rows={3}
                  placeholder="Practical takeaway for CAs, businesses, taxpayers…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">TaxSetu AI recommendation *</label>
                <textarea
                  value={form.recommendation}
                  onChange={(e) => setForm({ ...form, recommendation: e.target.value })}
                  rows={3}
                  placeholder="Actionable compliance advice, timelines, filings…"
                  className="w-full rounded-md bg-slate-900 border border-slate-700 text-white p-3 text-sm resize-y"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 block mb-1.5">Teaser (short line for ticker; blank = auto)</label>
                <Input
                  value={form.teaser}
                  onChange={(e) => setForm({ ...form, teaser: e.target.value })}
                  placeholder="One-line summary shown on cards & ticker"
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-800">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button onClick={submit} disabled={saving} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" data-testid="blog-manager-form-submit">
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Saving…" : editingSlug ? "Update" : "Publish"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
